import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './DashboardPage.css';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import TokenIcon from '../components/TokenIcon';

const DashboardPage = ({ user, onLogout, onAvailableTypesChange }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // 관리자용 상태
  const [emailList, setEmailList] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState('');
  // 투자 유형 선택 상태
  const [investmentType, setInvestmentType] = useState('crypto');
  // 사용 가능한 투자 유형들
  const [availableTypes, setAvailableTypes] = useState([]);
  // URL 파라미터 처리
  const [searchParams] = useSearchParams();
  // 밸런스 차트 데이터
  const [balanceChartData, setBalanceChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  // 자산 표시 순서 (자산 설정 연동)
  const [assetOrderSymbols, setAssetOrderSymbols] = useState([]);
  const [expandedLotSymbol, setExpandedLotSymbol] = useState(null);

  // 자산 설정에서 순서를 불러오기
  useEffect(() => {
    const loadAssetOrder = async () => {
      try {
        const token = safeGetItem('authToken');
        const response = await axios.get('/api/asset-configs', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          // 활성 자산만 순서대로 보관
          const symbols = response.data.data
            .filter(item => item && (item.is_active === undefined || item.is_active))
            .map(item => item.symbol);
          setAssetOrderSymbols(symbols);
        }
      } catch (e) {
        // 실패 시 무시하고 기본 정렬 유지
      }
    };
    loadAssetOrder();
  }, []);

  useEffect(() => {
    setExpandedLotSymbol(null);
  }, [selectedEmail, investmentType, userData?.email]);

  useEffect(() => {
    if (user?.type === 'admin') {
      fetchEmailList();
    }
  }, [user]);

  useEffect(() => {
    // URL에서 이메일 파라미터 확인
    const urlEmail = searchParams.get('email');
    const urlDataType = searchParams.get('dataType');
    
    if (urlEmail && user?.type === 'admin') {
      setSelectedEmail(urlEmail);
    }
    
    // URL에서 데이터 타입이 있으면 투자 유형 설정
    if (urlDataType && user?.type === 'admin') {
      if (urlDataType === 'legacy') {
        setInvestmentType('crypto');
      } else if (urlDataType === 'preferred-return') {
        setInvestmentType('preferred-return');
      } else if (urlDataType === 'fixed-income') {
        setInvestmentType('fixed-income');
      }
    }
  }, [searchParams, user]);

  useEffect(() => {
    // URL에서 이메일 파라미터 확인
    const urlEmail = searchParams.get('email');
    
    // admin 사용자이고 URL 파라미터에 이메일이 있으면 해당 이메일 사용
    if (user?.type === 'admin' && urlEmail) {
      fetchUserData(urlEmail);
    } else if (user?.type === 'admin' && selectedEmail) {
      // admin 사용자가 이메일 선택기를 통해 이메일을 선택한 경우
      fetchUserData(selectedEmail);
    } else if (user?.type === 'admin' && !selectedEmail && !urlEmail) {
      // admin 사용자가 자신의 데이터를 볼 때
      fetchUserData();
    } else if (user?.type !== 'admin') {
      // 일반 사용자
      fetchUserData();
    }
  }, [user, selectedEmail, searchParams]);

  // 사용자의 기본 데이터 타입에 따라 투자 유형 설정
  useEffect(() => {
    // URL에서 데이터 타입이 있으면 우선적으로 사용
    const urlDataType = searchParams.get('dataType');
    if (urlDataType) {
      if (urlDataType === 'legacy') {
        setInvestmentType('crypto');
      } else if (urlDataType === 'preferred-return') {
        setInvestmentType('preferred-return');
      } else if (urlDataType === 'fixed-income') {
        setInvestmentType('fixed-income');
      }
      return; // URL 파라미터가 있으면 다른 설정 무시
    }
    
    if (user?.default_data_type && user?.available_data_types) {
      // 사용자의 기본 데이터 타입에 따라 투자 유형 설정
      if (user.default_data_type === 'legacy') {
        setInvestmentType('crypto');
      } else if (user.default_data_type === 'preferred-return') {
        setInvestmentType('preferred-return');
      } else if (user.default_data_type === 'fixed-income') {
        setInvestmentType('fixed-income');
      }
      
      // 사용 가능한 투자 유형 설정
      const types = [];
      if (user.available_data_types.includes('legacy')) types.push('crypto');
      if (user.available_data_types.includes('preferred-return')) types.push('preferred-return');
      if (user.available_data_types.includes('fixed-income')) types.push('fixed-income');
      
      setAvailableTypes(types);
      if (onAvailableTypesChange) {
        onAvailableTypesChange(types);
      }
    }
  }, [user, searchParams]);

  // 투자 유형이 변경될 때 해당 타입의 데이터를 로드
  useEffect(() => {
    if (userData && userData.user_types && investmentType) {
      setError(''); // 에러 상태 초기화
      const userTypes = userData.user_types;
      let selectedTypeData = null;
      
      if (investmentType === 'crypto') {
        // Digital Assets Investments: legacy 타입 찾기
        selectedTypeData = userTypes.find(type => type.type === 'legacy');
      } else if (investmentType === 'preferred-return') {
        // Preferred Return Investments
        selectedTypeData = userTypes.find(type => type.type === 'preferred-return');
      } else if (investmentType === 'fixed-income') {
        // Fixed Income Investments
        selectedTypeData = userTypes.find(type => type.type === 'fixed-income');
      }
      
      if (selectedTypeData) {
        // 동적 토큰 가격 필드 병합 (_token_price로 끝나는 모든 키)
        const dynamicTokenPrices = Object.fromEntries(
          Object.entries(selectedTypeData || {})
            .filter(([key, value]) => key.endsWith('_token_price'))
        );

        setUserData(prevData => ({
          ...prevData,
          type: selectedTypeData.type,
          data: selectedTypeData.data,
          // legacy 타입의 경우 추가 정보들도 포함 + 동적 토큰 가격 필드 병합
          ...(selectedTypeData.type === 'legacy' && {
            total_balance_usd: selectedTypeData.total_balance_usd,
            vebe_quantity: selectedTypeData.vebe_quantity,
            veee_quantity: selectedTypeData.veee_quantity,
            vebe_btc_value: selectedTypeData.vebe_btc_value,
            veee_eth_value: selectedTypeData.veee_eth_value,
            token_lots: selectedTypeData.token_lots || [],
            ...dynamicTokenPrices
          })
        }));
        
        // legacy 타입인 경우 차트 데이터도 가져오기
        if (selectedTypeData.type === 'legacy') {
          const email = user?.type === 'admin' && selectedEmail ? selectedEmail : user?.email;
          console.log('Fetching balance chart data for email:', email);
          if (email) {
            const currentBalance = Number(selectedTypeData.total_balance_usd ?? userData?.total_balance_usd);
            fetchBalanceChartData(email, Number.isFinite(currentBalance) ? currentBalance : undefined);
          }
        }
      } else {
        setError('No data available for selected investment type.');
      }
    }
  }, [investmentType, user?.email, selectedEmail, user?.type]);

  // 관리자가 이메일을 선택할 때 투자 유형 초기화
  useEffect(() => {
    if (user?.type === 'admin' && selectedEmail) {
      // URL 파라미터가 있으면 그것을 우선 사용
      const urlDataType = searchParams.get('dataType');
      if (urlDataType) {
        if (urlDataType === 'legacy') {
          setInvestmentType('crypto');
        } else if (urlDataType === 'preferred-return') {
          setInvestmentType('preferred-return');
        } else if (urlDataType === 'fixed-income') {
          setInvestmentType('fixed-income');
        }
      } else {
        setInvestmentType('crypto'); // 기본값으로 설정
      }
    }
  }, [user?.type, selectedEmail, searchParams]);

  // 관리자가 이메일을 선택할 때 사용 가능한 타입에 따라 투자 유형 조정
  useEffect(() => {
    if (user?.type === 'admin' && availableTypes && availableTypes.length > 0) {
      // URL 파라미터가 있으면 조정하지 않음
      const urlDataType = searchParams.get('dataType');
      if (urlDataType) {
        return;
      }
      
      // 현재 선택된 투자 유형이 사용 가능한지 확인
      const hasLegacy = availableTypes.includes('legacy');
      const hasPreferredReturn = availableTypes.includes('preferred-return');
      const hasFixedIncome = availableTypes.includes('fixed-income');
      
      if (investmentType === 'crypto' && !hasLegacy) {
        // crypto가 선택되어 있지만 legacy 타입이 없으면 다른 타입으로 변경
        if (hasPreferredReturn) {
          setInvestmentType('preferred-return');
        } else if (hasFixedIncome) {
          setInvestmentType('fixed-income');
        }
      } else if (investmentType === 'preferred-return' && !hasPreferredReturn) {
        // preferred-return이 선택되어 있지만 해당 타입이 없으면 다른 타입으로 변경
        if (hasLegacy) {
          setInvestmentType('crypto');
        } else if (hasFixedIncome) {
          setInvestmentType('fixed-income');
        }
      } else if (investmentType === 'fixed-income' && !hasFixedIncome) {
        // fixed-income이 선택되어 있지만 해당 타입이 없으면 다른 타입으로 변경
        if (hasLegacy) {
          setInvestmentType('crypto');
        } else if (hasPreferredReturn) {
          setInvestmentType('preferred-return');
        }
      }
    }
  }, [user?.type, availableTypes, investmentType, searchParams]);

  // localStorage 안전한 읽기 함수
  const safeGetItem = (key) => {
    try {
      return localStorage.getItem(key) || sessionStorage.getItem(key);
    } catch (error) {
      console.error('localStorage getItem failed:', error);
      try {
        return sessionStorage.getItem(key);
      } catch (sessionError) {
        console.error('sessionStorage getItem also failed:', sessionError);
        return null;
      }
    }
  };

  // 네트워크 요청 재시도 함수
  const retryRequest = async (requestFn, maxRetries = 3, delay = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await requestFn();
      } catch (error) {
        if (i === maxRetries - 1) throw error;
        
        // 네트워크 오류인 경우에만 재시도
        if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
          console.log(`Request failed, retrying in ${delay}ms... (attempt ${i + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          delay *= 2; // 지수 백오프
        } else {
          throw error;
        }
      }
    }
  };

  const fetchEmailList = async () => {
    try {
      const token = safeGetItem('authToken');
      await retryRequest(async () => {
        const res = await axios.get('/api/emails', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) setEmailList(res.data.emails);
      });
    } catch (e) { /* 무시 */ }
  };

  // 밸런스 차트 데이터 가져오기
  const fetchBalanceChartData = async (email, currentBalanceOverride) => {
    try {
      setChartLoading(true);
      const token = safeGetItem('authToken');
      
      // 최근 5개월 데이터 요청
      const endTime = new Date();
      const startTime = new Date();
      startTime.setMonth(startTime.getMonth() - 5);
      
      // YYYY-MM-DD 형식으로 변환
      const startTimeStr = startTime.toISOString().split('T')[0];
      const endTimeStr = endTime.toISOString().split('T')[0];
      
      console.log('Fetching balance chart data:', {
        email,
        startTime: startTimeStr,
        endTime: endTimeStr
      });
      
      const response = await axios.get(`/api/external/legacy/${encodeURIComponent(email)}/balance-chart`, {
        params: {
          start_time: startTimeStr,
          end_time: endTimeStr
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Balance chart API response:', response.data);
      
      if (response.data && response.data.items && response.data.items.length > 0) {
        // 5개월간의 월별 집계: 첫날 값과 마지막 날 값을 모두 저장 + 월 P&L 합산
        const monthlyData = {};
        response.data.items.forEach(item => {
          const date = new Date(item.day);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          const dayTime = new Date(item.day).getTime();
          if (!monthlyData[monthKey]) {
            monthlyData[monthKey] = {
              month: monthKey,
              firstDay: item.day,
              lastDay: item.day,
              firstTotal: Number(item.total_balance_usd || 0),
              lastTotal: Number(item.total_balance_usd || 0),
              pnl_usd_sum: 0
            };
          }
          // 월간 P&L 누적
          monthlyData[monthKey].pnl_usd_sum += Number(item.pnl_usd || 0);
          // 첫날 업데이트
          if (new Date(monthlyData[monthKey].firstDay).getTime() > dayTime) {
            monthlyData[monthKey].firstDay = item.day;
            monthlyData[monthKey].firstTotal = Number(item.total_balance_usd || 0);
          }
          // 마지막 날 업데이트
          if (new Date(monthlyData[monthKey].lastDay).getTime() < dayTime) {
            monthlyData[monthKey].lastDay = item.day;
            monthlyData[monthKey].lastTotal = Number(item.total_balance_usd || 0);
          }
        });
        
        // 월별 데이터를 배열로 변환하고 정렬
        let chartData = Object.values(monthlyData)
          .map(d => ({
            month: d.month,
            // 기본 표현은 마지막 날 기준으로 둠 (정렬용)
            day: d.lastDay,
            // 기본 total은 마지막 날 값으로 설정 (후속 리샘플에서 사용 맵을 따로 만듦)
            total_balance_usd: d.lastTotal,
            pnl_usd: d.pnl_usd_sum
          }))
          .sort((a, b) => new Date(a.day) - new Date(b.day));
        
        // 현재 월의 데이터를 실제 사용자 데이터로 교체
        const currentDate = new Date();
        const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
        
        // 현재 월 데이터가 있는지 확인하고, 있다면 총액만 실제 사용자 데이터로 교체 (P&L은 월 누적값 유지)
        const currentMonthIndex = chartData.findIndex(item => item.month === currentMonth);
        if (userData && userData.total_balance_usd !== undefined) {
          if (currentMonthIndex !== -1) {
            chartData[currentMonthIndex] = {
              ...chartData[currentMonthIndex],
              total_balance_usd: userData.total_balance_usd,
              isCurrentMonth: true
            };
          } else {
            // 현재 월 데이터가 API에 없는 경우: 총액만 현재값, P&L은 0으로 시작
            chartData.push({
              month: currentMonth,
              day: currentDate.toISOString().split('T')[0],
              total_balance_usd: userData.total_balance_usd,
              pnl_usd: 0,
              isCurrentMonth: true
            });
          }
        }
        
        // 현재 월만 있는 경우, 이전 월 0값 엔트리를 추가하여 영역 차트가 그려지도록 처리
        if (Array.isArray(chartData) && chartData.length === 1 && chartData[0].month === currentMonth) {
          const prevDate = new Date(currentDate);
          prevDate.setMonth(prevDate.getMonth() - 1);
          const prevMonth = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
          // 이전 달의 마지막 날을 day로 설정 (정렬 안정성)
          const lastDayPrevMonth = new Date(prevDate.getFullYear(), prevDate.getMonth() + 1, 0);
          const prevDayStr = lastDayPrevMonth.toISOString().split('T')[0];
          chartData.unshift({
            month: prevMonth,
            day: prevDayStr,
            total_balance_usd: 0,
            pnl_usd: 0,
            isPaddingZeroMonth: true
          });
        }

        // 리샘플 규칙 적용:
        // - 각 월: 해당 월의 마지막 날 balance 사용 (없으면 첫날 값)
        // - 현재 월: 현재 잔고 사용
        // - 해당 월 값이 없으면 0
        if (Array.isArray(chartData) && chartData.length > 0) {
          // 원본 월별 첫날/마지막날 총액 맵 구성
          const monthToFirstTotal = Object.fromEntries(
            Object.values(monthlyData).map(d => [d.month, Number(d.firstTotal || 0)])
          );
          const monthToLastTotal = Object.fromEntries(
            Object.values(monthlyData).map(d => [d.month, Number(d.lastTotal || 0)])
          );

          // 연속 월 시퀀스 생성: 시작월 ~ 현재월
          const parseMonth = (ym) => {
            const [yy, mm] = ym.split('-').map(n => Number(n));
            return { year: yy, month: mm };
          };
          const formatMonth = (y, m) => `${y}-${String(m).padStart(2, '0')}`;
          const incMonth = (y, m) => {
            if (m === 12) return { year: y + 1, month: 1 };
            return { year: y, month: m + 1 };
          };
          const decMonth = (y, m) => {
            if (m === 1) return { year: y - 1, month: 12 };
            return { year: y, month: m - 1 };
          };

          const firstDataMonth = chartData[0].month;
          const { year: startY0, month: startM0 } = parseMonth(firstDataMonth);
          const { year: curY, month: curM } = parseMonth(currentMonth);

          // 한 달만 있는 경우라도, 이전 달을 포함하여 최소 2포인트 보장
          let seqStart = { year: startY0, month: startM0 };
          if (startY0 === curY && startM0 === curM) {
            seqStart = decMonth(curY, curM);
          }

          const monthsSeq = [];
          let cursor = { ...seqStart };
          while (cursor.year < curY || (cursor.year === curY && cursor.month <= curM)) {
            monthsSeq.push(formatMonth(cursor.year, cursor.month));
            cursor = incMonth(cursor.year, cursor.month);
          }

          // 각 월에 대해 리샘플 값 계산
          const remapped = monthsSeq.map((ym) => {
            const { year, month } = parseMonth(ym);
            const isCurrent = (ym === currentMonth);
            let value;
            if (isCurrent) {
              // 현재 잔고 우선, 없으면 해당 월의 마지막 값 → 첫날 값 → 0
              const currentBalance = (currentBalanceOverride !== undefined && currentBalanceOverride !== null)
                ? currentBalanceOverride
                : userData?.total_balance_usd;
              if (Number.isFinite(Number(currentBalance))) {
                value = Number(currentBalance);
              } else if (Number.isFinite(monthToLastTotal[ym])) {
                value = Number(monthToLastTotal[ym] || 0);
              } else if (Number.isFinite(monthToFirstTotal[ym])) {
                value = Number(monthToFirstTotal[ym] || 0);
              } else {
                value = 0;
              }
            } else {
              // 해당 월의 마지막 날 값 사용, 없으면 첫날 값, 그것도 없으면 0
              if (Number.isFinite(monthToLastTotal[ym])) {
                value = Number(monthToLastTotal[ym] || 0);
              } else if (Number.isFinite(monthToFirstTotal[ym])) {
                value = Number(monthToFirstTotal[ym] || 0);
              } else {
                value = 0;
              }
            }
            // day는 해당 월의 말일로 설정
            const lastDay = new Date(year, month, 0);
            const dayStr = lastDay.toISOString().split('T')[0];
            return {
              month: ym,
              day: dayStr,
              total_balance_usd: value,
              pnl_usd: 0
            };
          });

          console.log('Processed chart data (resampled):', remapped);
          setBalanceChartData(remapped);
        } else {
          console.log('Processed chart data:', chartData);
          setBalanceChartData(chartData);
        }
      } else {
        console.log('No balance chart data received or empty items array');
        setBalanceChartData([]);
      }
    } catch (error) {
      console.error('Balance chart fetch error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setBalanceChartData([]);
    } finally {
      setChartLoading(false);
    }
  };

  // email 파라미터가 있으면 해당 유저, 없으면 내 정보
  const fetchUserData = async (emailParam) => {
    try {
      setIsLoading(true);
      setError('');
      const token = safeGetItem('authToken');
      
      // URL에서 해시 ID와 데이터 타입 확인
      const urlHashId = searchParams.get('hash_id');
      const urlDataType = searchParams.get('dataType');
      
      let url = '/api/user/data';
      if (user?.type === 'admin' && emailParam) {
        if (urlHashId) {
          // 미리보기 모드: 해시 ID가 있을 때 미리보기 엔드포인트 사용
          url = `/api/external/users/${encodeURIComponent(emailParam)}/preview`;
          if (urlDataType) {
            url += `?data_type=${urlDataType}&hash_id=${urlHashId}`;
          } else {
            url += `?hash_id=${urlHashId}`;
          }
        } else {
          // 일반 모드: 기존 엔드포인트 사용
          url = `/api/user/data?email=${encodeURIComponent(emailParam)}`;
        }
      }

      await retryRequest(async () => {
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // 미리보기 모드와 일반 모드의 응답 구조가 다를 수 있으므로 처리
        let responseData;
        if (user?.type === 'admin' && emailParam && urlHashId) {
          // 미리보기 모드: 직접 응답 데이터 사용
          responseData = response.data;
        } else {
          // 일반 모드: 기존 구조 사용
          if (response.data.success) {
            responseData = response.data.data;
          } else {
            throw new Error('Failed to load user data');
          }
        }
        
        if (responseData.user_types) {
          // 새로운 구조: user_types 배열이 있는 경우
          const userTypes = responseData.user_types;
          // 사용 가능한 타입들 설정
          const types = userTypes.map(type => type.type);
          setAvailableTypes(types);
          if (onAvailableTypesChange) {
            onAvailableTypesChange(types);
          }

          // URL 파라미터에서 dataType이 있으면 우선적으로 사용
          const urlDataType = searchParams.get('dataType');
          let defaultType = null;
          let defaultTypeData = null;
          
          // 1. URL 파라미터의 dataType이 있으면 우선적으로 사용
          if (urlDataType) {
            const urlTypeData = userTypes.find(type => type.type === urlDataType);
            if (urlTypeData) {
              defaultType = urlTypeData.type;
              defaultTypeData = urlTypeData;
            }
          }
          
          // 2. URL 파라미터가 없으면 사용자의 기본 데이터 타입 확인
          if (!defaultType && user?.default_data_type) {
            const userDefaultType = userTypes.find(type => type.type === user.default_data_type);
            if (userDefaultType) {
              defaultType = userDefaultType.type;
              defaultTypeData = userDefaultType;
            }
          }
          
          // 3. 기본 데이터 타입이 없으면 값이 0이 아닌 타입을 찾음
          if (!defaultType) {
            for (const typeObj of userTypes) {
              // legacy 타입: 주요 자산이 모두 0인지 확인
              if (typeObj.type === 'legacy') {
                const d = typeObj.data[0] || {};
                const assetKeys = [
                  'ETH Balance', 'BTC Balance', 'USDT Balance', 'USDC Balance', 'KRW Balance', 'VEEE Quantity', 'VEBE Quantity'
                ];
                const hasNonZero = assetKeys.some(key => parseFloat(d[key] || '0') > 0);
                if (hasNonZero) {
                  defaultType = typeObj.type;
                  defaultTypeData = typeObj;
                  break;
                }
              } else {
                // 투자 상품 타입: data 배열의 Current Balance, principal 등
                const d = typeObj.data;
                const hasNonZero = Array.isArray(d) && d.some(item => {
                  const principal = parseFloat((item.principal || '0').toString().replace(/[^\d.\-]/g, ''));
                  const balance = parseFloat((item['Current Balance'] || '0').toString().replace(/[^\d.\-]/g, ''));
                  return principal > 0 || balance > 0;
                });
                if (hasNonZero) {
                  defaultType = typeObj.type;
                  defaultTypeData = typeObj;
                  break;
                }
              }
            }
          }
          
          // 4. 모두 0이면 첫 번째 타입 사용 (기본 데이터 타입이 있으면 그것을 우선)
          if (!defaultType) {
            if (user?.default_data_type) {
              // 사용자의 기본 데이터 타입이 있으면 그것을 사용 (0값이어도)
              const userDefaultType = userTypes.find(type => type.type === user.default_data_type);
              if (userDefaultType) {
                defaultType = userDefaultType.type;
                defaultTypeData = userDefaultType;
              } else {
                // 기본 데이터 타입이 user_types에 없으면 첫 번째 타입 사용
                defaultType = types[0];
                defaultTypeData = userTypes[0];
              }
            } else {
              // 기본 데이터 타입이 없으면 첫 번째 타입 사용
              defaultType = types[0];
              defaultTypeData = userTypes[0];
            }
          }
          
          const defaultInvestmentType = defaultType === 'legacy' ? 'crypto' : 
                                       defaultType === 'preferred-return' ? 'preferred-return' : 
                                       defaultType === 'fixed-income' ? 'fixed-income' : 'crypto';
          setInvestmentType(defaultInvestmentType);
          if (defaultTypeData) {
            // 동적 토큰 가격 필드 병합 (_token_price로 끝나는 모든 키)
            const dynamicTokenPrices = Object.fromEntries(
              Object.entries(defaultTypeData || {})
                .filter(([key, value]) => key.endsWith('_token_price'))
            );

            setUserData({
              type: defaultTypeData.type,
              data: defaultTypeData.data,
              user_types: userTypes, // 전체 user_types 저장
              ...(defaultTypeData.type === 'legacy' && {
                total_balance_usd: defaultTypeData.total_balance_usd,
                vebe_quantity: defaultTypeData.vebe_quantity,
                veee_quantity: defaultTypeData.veee_quantity,
                vebe_btc_value: defaultTypeData.vebe_btc_value,
                veee_eth_value: defaultTypeData.veee_eth_value,
                token_lots: defaultTypeData.token_lots || [],
                ...dynamicTokenPrices
              })
            });
            
            // legacy 타입인 경우 차트 데이터도 가져오기 (새로운 구조)
            if (defaultTypeData.type === 'legacy') {
              const email = user?.type === 'admin' && emailParam ? emailParam : user?.email;
              console.log('Fetching balance chart data for email (new structure):', email);
              if (email) {
                const currentBalance = Number(defaultTypeData.total_balance_usd ?? userData?.total_balance_usd);
                fetchBalanceChartData(email, Number.isFinite(currentBalance) ? currentBalance : undefined);
              }
            }
          } else {
            setError('No data available for this user.');
          }
        } else {
          // 기존 구조: 직접 data와 type이 있는 경우
          setAvailableTypes([responseData.type]);
          if (onAvailableTypesChange) {
            onAvailableTypesChange([responseData.type]);
          }
          setUserData(responseData);
          
          // legacy 타입인 경우 차트 데이터도 가져오기
          if (responseData.type === 'legacy') {
            const email = user?.type === 'admin' && emailParam ? emailParam : user?.email;
            console.log('Fetching balance chart data for email (fetchUserData):', email);
            if (email) {
              const currentBalance = Number(responseData.total_balance_usd ?? userData?.total_balance_usd);
              fetchBalanceChartData(email, Number.isFinite(currentBalance) ? currentBalance : undefined);
            }
          }
        }
      });
    } catch (err) {
      console.error('User data fetch error:', err);
      
      // 네트워크 연결 문제 감지
      if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
        setError('Network connection failed. Please check your internet connection.');
      } else if (err.response?.status === 401) {
        setError('Authentication expired. Please log in again.');
        // 로그아웃 처리
        onLogout();
      } else if (err.response?.status === 404) {
        setError('User data not found.');
      } else if (err.response?.status === 500) {
        setError('Server error occurred. Please try again later.');
      } else {
        setError('Failed to load user data.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '0';
    
    // Convert to string if it's not already
    const stringValue = String(value);
    
    // Remove currency symbols and commas, then parse
    const cleanValue = stringValue.replace(/[₩$,]/g, '');
    const numValue = parseFloat(cleanValue);
    
    if (isNaN(numValue)) return stringValue;
    
    // Format based on currency type
    if (stringValue.includes('₩')) {
      return new Intl.NumberFormat('ko-KR', {
        style: 'currency',
        currency: 'KRW'
      }).format(numValue);
    } else if (stringValue.includes('$')) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(numValue);
    }
    
    return numValue.toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTypeIcon = (type) => {
    const icons = {
      'legacy': '💰',
      'fixed-income': '📈',
      'preferred-return': '🏆'
    };
    return icons[type] || '📊';
  };

  const getTypeColor = (type) => {
    const colors = {
      'legacy': '#0066CC',
      'fixed-income': '#22c55e',
      'preferred-return': '#f97316'
    };
    return colors[type] || '#6b7280';
  };

  const getTypeTitle = (type) => {
    const titles = {
      'legacy': 'Portfolio Snapshot',
      'fixed-income': 'Fixed Income Products',
      'preferred-return': 'Preferred Return Products'
    };
    return titles[type] || 'Investment Portfolio';
  };

  const getTypeSubtitle = (type) => {
    const subtitles = {
      'legacy': '',
      'fixed-income': 'Fixed income investment portfolio for',
      'preferred-return': 'Preferred return investment portfolio for'
    };
    return subtitles[type] || 'Investment portfolio for';
  };

  if (isLoading) {
    return (
      <div className="dashboard-page">
        <div className="loading-container">
          <div className="loading-skeleton"></div>
          <div className="loading-skeleton"></div>
          <div className="loading-skeleton"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page">
        <div className="error-container">
          <div className="error-alert">
            <span>⚠️</span>
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="dashboard-page">
        <div className="error-container">
          <div className="error-alert">
            <span>⚠️</span>
            No data available for this user.
          </div>
        </div>
      </div>
    );
  }

  // Render based on data type
  const renderLegacyDashboard = () => {
    const balanceData = userData.data[0];
    
    const balanceItems = [
      { key: 'ETH Balance', label: 'Ethereum', currency: 'ETH' },
      { key: 'BTC Balance', label: 'Bitcoin', currency: 'BTC' },
      { key: 'USDT Balance', label: 'USDT', currency: 'USDT' },
      { key: 'USDC Balance', label: 'USDC', currency: 'USDC' },
      { key: 'KRW Balance', label: 'KRW', currency: 'KRW' },
      { key: 'VEEE Quantity', label: 'VEEE', currency: 'VEEE' },
      { key: 'VEBE Quantity', label: 'VEBE', currency: 'VEBE' },
    ];

    // Filter out items with zero balance
    const nonZeroBalanceItems = balanceItems.filter(item => {
      const value = parseFloat(balanceData?.[item.key] || '0');
      return value > 0;
    });

    if (nonZeroBalanceItems.length === 0) {
      return (
        <div className="no-balance-container">
          <div className="no-balance-icon">💰</div>
          <h2 className="no-balance-title">No Assets Found</h2>
          <p className="no-balance-message">
            No crypto investment assets were found for this account. All balances are currently zero.
          </p>
        </div>
      );
    }

    // Calculate total assets value
    const totalAssets = nonZeroBalanceItems.reduce((total, item) => {
      const value = parseFloat(balanceData?.[item.key] || '0');
      let additionalPrice = 0;
      
      if (item.currency === 'BTC' && userData.btc_token_price && value > 0) {
        additionalPrice = value * userData.btc_token_price;
      } else if (item.currency === 'ETH' && userData.eth_token_price && value > 0) {
        additionalPrice = value * userData.eth_token_price;
      } else if (item.currency === 'VEBE' && userData.vebe_btc_value && userData.btc_token_price && value > 0) {
        additionalPrice = userData.vebe_btc_value * userData.btc_token_price;
      } else if (item.currency === 'VEEE' && userData.veee_eth_value && userData.eth_token_price && value > 0) {
        additionalPrice = userData.veee_eth_value * userData.eth_token_price;
      } else if (item.currency === 'USDT' || item.currency === 'USDC') {
        additionalPrice = value;
      } else if (item.currency === 'KRW') {
        additionalPrice = value / 1300; // Approximate USD conversion
      }
      
      return total + additionalPrice;
    }, 0);

    // Calculate asset distribution
    const assetDistribution = nonZeroBalanceItems.map(item => {
      const value = parseFloat(balanceData?.[item.key] || '0');
      let additionalPrice = 0;
      
      if (item.currency === 'BTC' && userData.btc_token_price && value > 0) {
        additionalPrice = value * userData.btc_token_price;
      } else if (item.currency === 'ETH' && userData.eth_token_price && value > 0) {
        additionalPrice = value * userData.eth_token_price;
      } else if (item.currency === 'VEBE' && userData.vebe_btc_value && userData.btc_token_price && value > 0) {
        additionalPrice = userData.vebe_btc_value * userData.btc_token_price;
      } else if (item.currency === 'VEEE' && userData.veee_eth_value && userData.eth_token_price && value > 0) {
        additionalPrice = userData.veee_eth_value * userData.eth_token_price;
      } else if (item.currency === 'USDT' || item.currency === 'USDC') {
        additionalPrice = value;
      } else if (item.currency === 'KRW') {
        additionalPrice = value / 1300;
      }
      
      const percentage = totalAssets > 0 ? (additionalPrice / totalAssets) * 100 : 0;
      
      return {
        name: item.label,
        value: additionalPrice,
        percentage: percentage,
        currency: item.currency
      };
    }).sort((a, b) => b.value - a.value);

    const lotsBySymbol = (Array.isArray(userData.token_lots) ? userData.token_lots : []).reduce((acc, lot) => {
      const symbol = (lot?.symbol || '').toUpperCase();
      if (!symbol) return acc;
      if (!acc[symbol]) acc[symbol] = [];
      acc[symbol].push(lot);
      return acc;
    }, {});

    const sortLotsByTime = (lots) => {
      return [...lots].sort((a, b) => {
        const at = a?.received_at ? new Date(a.received_at).getTime() : Number.MAX_SAFE_INTEGER;
        const bt = b?.received_at ? new Date(b.received_at).getTime() : Number.MAX_SAFE_INTEGER;
        if (at !== bt) return at - bt;
        return Number(a?.sequence || 0) - Number(b?.sequence || 0);
      });
    };

    const formatLotAmount = (quantity) => Number(quantity || 0).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 8
    });

    // Sparkline Y-axis unit label based on max value
    const maxChartY = Array.isArray(balanceChartData) && balanceChartData.length > 0
      ? Math.max(...balanceChartData.map(d => Number(d.total_balance_usd) || 0))
      : 0;
    // Determine Y-axis step based on top value with special ranges
    let yStep;
    if (maxChartY >= 500000 && maxChartY <= 1000000) {
      yStep = 200000; // 200K$ when 500K~1000K
    } else if (maxChartY >= 50000 && maxChartY <= 100000) {
      yStep = 20000; // 20K$ when 50K~100K
    } else if (maxChartY >= 5000 && maxChartY <= 10000) {
      yStep = 2000; // 2K$ when 5K~10K
    } else if (maxChartY > 100000) {
      yStep = 100000; // default large range
    } else if (maxChartY > 10000) {
      yStep = 10000;
    } else {
      yStep = 1000;
    }
    const yMaxTick = Math.ceil(maxChartY / yStep) * yStep || yStep;

    return (
      <div className="legacy-dashboard">
        {/* Portfolio Summary (Top row) */}
        <div className="portfolio-summary-row">
          {/* Total Portfolio with right-side chart */}
          <div className="portfolio-card">
            <div className="portfolio-row">
              <div className="portfolio-info">
                <div className="portfolio-card-header">
                  <div className="title-row">
                    <span className="muted">Total Portfolio</span>
                    <span className="currency-chip">USD</span>
                  </div>
                  <div className="portfolio-main-amount">
                    {typeof userData.total_balance_usd === 'number' ?
                      `$${userData.total_balance_usd.toLocaleString('en-US', { maximumFractionDigits: 1 })}` : '-'}
                  </div>
                  {/* crypto prices moved to middle column */}
                </div>
                {/* Asset Distribution moved under total balance */}
                <div className="asset-distribution-compact" style={{ marginTop: 12 }}>
                  <div className="section-title-inline">Asset Distribution</div>
                  <div className="distribution-chart">
                    <div className="donut-chart">
                      <svg width="120" height="120" viewBox="0 0 120 120">
                        <circle cx="60" cy="60" r="50" fill="none" stroke="#f3f4f6" strokeWidth="20" />
                        {assetDistribution.length === 1 ? (
                          (() => {
                            const colors = ['#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#f97316'];
                            const color = colors[0];
                            return (
                              <circle cx="60" cy="60" r="50" fill="none" stroke={color} strokeWidth="20" strokeLinecap="round" />
                            );
                          })()
                        ) : (
                          (() => {
                            const filteredAssets = assetDistribution.filter(a => a.percentage >= 0.01);
                            const totalPercentage = filteredAssets.reduce((sum, a) => sum + a.percentage, 0);
                            
                            // 만약 필터링된 자산이 하나만 있고 그 자산이 거의 100%에 가깝다면, 100%로 처리
                            if (filteredAssets.length === 1 && filteredAssets[0].percentage >= 99.99) {
                              const colors = ['#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#f97316'];
                              const color = colors[0];
                              return (
                                <circle cx="60" cy="60" r="50" fill="none" stroke={color} strokeWidth="20" strokeLinecap="round" />
                              );
                            }
                            
                            return filteredAssets.map((asset, index) => {
                              const colors = ['#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#f97316'];
                              const color = colors[index % colors.length];
                              const startAngle = filteredAssets.slice(0, index).reduce((sum, a) => sum + (a.percentage / totalPercentage) * 360, 0);
                              const endAngle = startAngle + (asset.percentage / totalPercentage) * 360;
                              const startRad = (startAngle - 90) * Math.PI / 180;
                              const endRad = (endAngle - 90) * Math.PI / 180;
                              const x1 = 60 + 50 * Math.cos(startRad);
                              const y1 = 60 + 50 * Math.sin(startRad);
                              const x2 = 60 + 50 * Math.cos(endRad);
                              const y2 = 60 + 50 * Math.sin(endRad);
                              const largeArcFlag = asset.percentage / totalPercentage > 0.5 ? 1 : 0;
                              const pathData = [`M ${x1} ${y1}`, `A 50 50 0 ${largeArcFlag} 1 ${x2} ${y2}`].join(' ');
                              return (
                                <path key={asset.currency} d={pathData} fill="none" stroke={color} strokeWidth="20" strokeLinecap="butt" />
                              );
                            });
                          })()
                        )}
                      </svg>
                      <div className="donut-center">
                        {totalAssets / 1000000 < 0.01 ? '<$0.01M' : `$${(totalAssets / 1000000).toFixed(2)}M`}
                      </div>
                    </div>
                    <div className="distribution-legend">
                      {assetDistribution
                        .filter((asset) => {
                          // 실제 수량 계산
                          const actualAmount = balanceData?.[`${asset.currency} Balance`] || 
                                             balanceData?.[`${asset.currency} Quantity`] || '0';
                          const actualAmountNum = parseFloat(actualAmount);
                          
                          // 최소값 설정
                          let minCeilingValue;
                          if (asset.currency === 'BTC') {
                            minCeilingValue = 0.0001;
                          } else if (asset.currency === 'ETH') {
                            minCeilingValue = 0.001;
                          } else {
                            minCeilingValue = 1;
                          }
                          
                          // 최소값 이상인 자산만 표시
                          return actualAmountNum >= minCeilingValue;
                        })
                        .map((asset, index) => {
                        const colors = ['#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#f97316'];
                        const color = colors[index % colors.length];
                        return (
                          <div key={asset.currency} className="legend-item">
                            <div className="legend-color" style={{ backgroundColor: color }}></div>
                            <span className="legend-name">{asset.name}</span>
                            <span className="legend-percentage">
                              {asset.percentage < 0.01 ? '<0.01%' : `${asset.percentage.toFixed(2)}%`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              {/* Middle: Balance Area Chart */}
              <div className="portfolio-sparkline">
                <div className="sparkline-wrap">
                  {balanceChartData && balanceChartData.length > 1 && (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={balanceChartData} margin={{ left: 8, right: 8, top: 10, bottom: 4 }}>
                        <XAxis 
                          dataKey="month" 
                          stroke="#7a8a99"
                          fontSize={13}
                          tickLine={false}
                          axisLine={{ stroke: 'rgba(255,255,255,0.12)' }}
                          ticks={balanceChartData.map(item => item.month)}
                          interval={0}
                          padding={{ left: 20, right: 20 }}
                          tick={{ textAnchor: 'middle' }}
                          tickFormatter={(value) => {
                            const [y, m] = value.split('-');
                            const d = new Date(Number(y), Number(m) - 1, 1);
                            return d.toLocaleString('en-US', { month: 'short' });
                          }}
                        />
                        <YAxis 
                          hide={false}
                          orientation="left"
                          axisLine={false}
                          tickLine={false}
                          stroke="#7a8a99"
                          fontSize={10}
                          domain={[0, yMaxTick]}
                          tickFormatter={(v) => {
                            if (v === undefined || v === null) return '';
                            const val = Number(v);
                            return `$${Math.round(val / 1000)}K`;
                          }}
                          allowDecimals={false}
                          scale="linear"
                          ticks={(() => {
                            const arr = [];
                            for (let v = yStep; v <= yMaxTick; v += yStep) arr.push(v);
                            return arr;
                          })()}
                          tickCount={Math.ceil(yMaxTick / yStep)}
                        />
                        <Tooltip formatter={(v) => [`$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`, 'Total']} />
                        <Area type="monotone" dataKey="total_balance_usd" stroke="#1dd3b0" strokeWidth={2} fill="#1dd3b0" fillOpacity={0.15} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
              {/* Right: Current Digital Asset Prices */}
              <div className="asset-distribution-compact">
                {(() => {
                  // userData의 _token_price 키를 모두 수집하여 동적 렌더링
                  let items = Object.entries(userData || {})
                    .filter(([key, value]) => key.endsWith('_token_price'))
                    .map(([key, value]) => {
                      const symbol = key.replace('_token_price', '').toUpperCase();
                      const num = typeof value === 'string' ? Number(value) : value;
                      return Number.isFinite(num) ? { symbol, price: num } : null;
                    })
                    .filter(Boolean);

                  // 자산 설정 순서대로 정렬 (없으면 맨 뒤)
                  if (Array.isArray(assetOrderSymbols) && assetOrderSymbols.length > 0) {
                    const fallbackIndex = assetOrderSymbols.length + 1000;
                    items.sort((a, b) => {
                      const ia = assetOrderSymbols.indexOf(a.symbol);
                      const ib = assetOrderSymbols.indexOf(b.symbol);
                      return (ia === -1 ? fallbackIndex : ia) - (ib === -1 ? fallbackIndex : ib);
                    });
                  }

                  // 상위 5개만 표시
                  items = items.slice(0, 5);

                  if (items.length === 0) return null;

                  return (
                    <div className="mini-price-box price-box-large" style={{ width: '100%' }}>
                      <div className="mini-price-title">Current Digital Asset Prices</div>
                      <div className="asset-price-list compact-5">
                        {items.map(item => (
                          <div key={item.symbol} className="asset-price-card small">
                            <div className="asset-info">
                              <TokenIcon symbol={item.symbol} size={20} className="asset-icon small" />
                              <span className="asset-symbol">{item.symbol}</span>
                            </div>
                            <div className="asset-price">${item.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
        
        

        {/* Bottom Row - Protocol Breakdown */}
        <div className="protocol-breakdown-card">
          <h3>Portfolio Overview</h3>
          <div className="protocol-table">
            <div className="table-header">
              <span>Name</span>
              <span>Share</span>
              <span>Amount</span>
              <span>Value</span>
            </div>
            {assetDistribution
              .filter((asset) => {
                // 실제 수량 계산
                const actualAmount = balanceData?.[`${asset.currency} Balance`] || 
                                   balanceData?.[`${asset.currency} Quantity`] || '0';
                const actualAmountNum = parseFloat(actualAmount);
                
                // 최소값 설정
                let minCeilingValue;
                if (asset.currency === 'BTC') {
                  minCeilingValue = 0.0001;
                } else if (asset.currency === 'ETH') {
                  minCeilingValue = 0.001;
                } else {
                  minCeilingValue = 1;
                }
                
                // 최소값 이상인 자산만 표시
                return actualAmountNum >= minCeilingValue;
              })
              .map((asset, index) => {
              // 실제 수량 계산
              const actualAmount = balanceData?.[`${asset.currency} Balance`] || 
                                 balanceData?.[`${asset.currency} Quantity`] || '0';
              
              // VEBE와 VEEE는 정수로, BTC는 4자리, 나머지는 소수점 포함 (불필요한 0 제거)
              const actualAmountNum = parseFloat(actualAmount);
              let formattedAmount;
              let minCeilingValue;
              
              if (asset.currency === 'BTC') {
                formattedAmount = actualAmountNum.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4
                });
                minCeilingValue = 0.0001;
              } else if (asset.currency === 'ETH') {
                formattedAmount = actualAmountNum.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 8
                });
                minCeilingValue = 0.001;
              } else {
                // VEBE, VEEE, USDT, USDC, KRW 등 기타 자산
                formattedAmount = actualAmountNum.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 8
                });
                minCeilingValue = 1;
              }
              
              const symbolLots = sortLotsByTime(lotsBySymbol[asset.currency] || []);
              const canExpandLots = symbolLots.length > 1;
              const isExpanded = canExpandLots && expandedLotSymbol === asset.currency;
              const lotTotalQuantity = symbolLots.reduce((sum, lot) => sum + Number(lot.quantity || 0), 0);

              return (
                <React.Fragment key={asset.currency}>
                  <div
                    className={`table-row ${canExpandLots ? 'expandable-row' : ''}`}
                    onClick={() => {
                      if (!canExpandLots) return;
                      setExpandedLotSymbol(prev => (prev === asset.currency ? null : asset.currency));
                    }}
                    style={canExpandLots ? { cursor: 'pointer' } : undefined}
                  >
                    <div className="protocol-name">
                      <span className="protocol-icon">
                        <TokenIcon symbol={asset.currency} size={24} />
                      </span>
                      <span className="asset-name">{asset.name}</span>
                      {canExpandLots && (
                        <button
                          type="button"
                          className={`expand-toggle ${isExpanded ? 'expanded' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedLotSymbol(prev => (prev === asset.currency ? null : asset.currency));
                          }}
                          aria-label={isExpanded ? `${asset.currency} lot 접기` : `${asset.currency} lot 펼치기`}
                        >
                          <span className={`expand-chevron ${isExpanded ? 'expanded' : ''}`}>▾</span>
                        </button>
                      )}
                    </div>
                    <div className="protocol-share">
                      <div className="share-bar">
                        <div className="share-fill" style={{ width: `${asset.percentage}%` }}></div>
                      </div>
                      <span>{asset.percentage < 0.01 ? '<0.01%' : `${asset.percentage.toFixed(2)}%`}</span>
                    </div>
                    <div className="protocol-amount">
                      {actualAmountNum === 0 ?
                        `<${minCeilingValue}` :
                        (actualAmountNum > 0 && actualAmountNum < minCeilingValue) ?
                        `<${minCeilingValue}` :
                        formattedAmount}
                    </div>
                    <div className="protocol-value">
                      ${asset.value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="table-row lot-group-row">
                      <div className="lot-subtable">
                        {symbolLots.map((lot) => {
                          const qty = Number(lot.quantity || 0);
                          const lotValueUsd = lotTotalQuantity > 0
                            ? (asset.value * qty) / lotTotalQuantity
                            : 0;
                          const lotValueText = Number.isFinite(lotValueUsd)
                            ? `$${lotValueUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : '-';
                          return (
                              <div key={lot.id || lot.key} className="table-row lot-detail-row">
                              <div className="protocol-name lot-name">
                                <span className="asset-name">{lot.key || `${lot.symbol}_${String(lot.sequence || 0).padStart(2, '0')}`}</span>
                              </div>
                              <div className="protocol-share lot-share-empty"></div>
                              <div className="protocol-amount">{formatLotAmount(qty)}</div>
                              <div className="protocol-value">{lotValueText}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Original Balance Grid */}
        <div className="balance-grid">
          {nonZeroBalanceItems
            .filter((item) => {
              const value = balanceData?.[item.key] || '0';
              const numValue = parseFloat(value);
              
              // 최소값 설정
              let minCeilingValue;
              if (item.currency === 'BTC') {
                minCeilingValue = 0.0001;
              } else if (item.currency === 'ETH') {
                minCeilingValue = 0.001;
              } else {
                minCeilingValue = 1;
              }
              
              // 최소값 이상인 자산만 표시
              return numValue >= minCeilingValue;
            })
            .map((item) => {
            const value = balanceData?.[item.key] || '0';
            const numValue = parseFloat(value);
            
            // Calculate additional price information
            let additionalPrice = null;
            let additionalInfo = null;
            if (item.currency === 'BTC' && userData.btc_token_price && numValue > 0) {
              additionalPrice = numValue * userData.btc_token_price;
            } else if (item.currency === 'ETH' && userData.eth_token_price && numValue > 0) {
              additionalPrice = numValue * userData.eth_token_price;
            } else if (item.currency === 'VEBE' && userData.vebe_btc_value && userData.btc_token_price && numValue > 0) {
              additionalPrice = userData.vebe_btc_value * userData.btc_token_price;
              additionalInfo = `(≈${userData.vebe_btc_value.toFixed(4)} BTC)`;
            } else if (item.currency === 'VEEE' && userData.veee_eth_value && userData.eth_token_price && numValue > 0) {
              additionalPrice = userData.veee_eth_value * userData.eth_token_price;
              additionalInfo = `(≈${userData.veee_eth_value.toFixed(4)} ETH)`;
            }
            
            return (
              <div key={item.key} className="balance-card">
                  <div className="balance-card-header">
                  <div className="balance-icon" style={{ backgroundColor: 'transparent' }}>
                    <TokenIcon symbol={item.currency} size={28} />
                  </div>
                  <div className="balance-info">
                    <span className="balance-currency">{item.currency}</span>
                  </div>
                </div>
                <div className="balance-amount">
                  <span className="balance-value">
                    ${additionalPrice ? additionalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                  </span>
                  <div className="balance-update">
                    {item.currency === 'USDT' || item.currency === 'USDC' ? '$' :
                     item.currency === 'KRW' ? '₩' : ''} 
                    {item.currency === 'BTC' ? 
                      parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 }) :
                      formatCurrency(value)
                    }
                    {item.currency === 'ETH' ? ' ETH' : 
                     item.currency === 'BTC' ? ' BTC' : 
                     item.currency === 'VEEE' ? ' VEEE' :
                     item.currency === 'VEBE' ? ' VEBE' : ''}
                    {additionalInfo && ` ${additionalInfo}`}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    );
  };

  const renderInvestmentDashboard = () => {
    const products = userData.data;
    const fallbackSummaryCurrency = userData.type === 'fixed-income' ? '₩' : '$';

    const detectSymbolFromValue = (value) => {
      if (value === null || value === undefined) return null;
      const text = String(value).trim().toUpperCase();
      if (!text) return null;
      if (text.includes('₩') || text.includes('KRW')) return '₩';
      if (text.includes('$') || text.includes('USD')) return '$';
      return null;
    };

    const detectProductCurrencySymbol = (product) => {
      const candidates = [
        product?.asset,
        product?.Asset,
        product?.currency,
        product?.Currency,
        product?.principal,
        product?.['Current Balance']
      ];
      for (const candidate of candidates) {
        const detected = detectSymbolFromValue(candidate);
        if (detected) return detected;
      }
      return null;
    };

    // 모든 상품이 0값인지 확인
    const allZeroProducts = products.every(product => {
      const principal = parseFloat(String(product.principal || '0').replace(/[₩$,]/g, ''));
      const balance = parseFloat(String(product['Current Balance'] || '0').replace(/[₩$,]/g, ''));
      return (isNaN(principal) || principal === 0) && (isNaN(balance) || balance === 0);
    });

    if (allZeroProducts) {
      return (
        <div className="no-balance-container">
          <div className="no-balance-icon">📊</div>
          <h2 className="no-balance-title">No Investment Products Found</h2>
          <p className="no-balance-message">
            No investment products were found for this account. All balances are currently zero.
          </p>
        </div>
      );
    }

    // 만기일이 지난 상품 제외 (요약 합계 전용)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eligibleProductsForSummary = Array.isArray(products)
      ? products.filter(product => {
          const maturityRaw = product['maturity date'] || product['Maturity Date'] || product['maturity_date'];
          if (!maturityRaw) return true; // 만기일 정보 없으면 포함
          const maturityDate = new Date(maturityRaw);
          if (isNaN(maturityDate)) return true; // 파싱 실패 시 포함
          maturityDate.setHours(0, 0, 0, 0);
          // 오늘보다 이전(지난) 만기일이면 제외, 오늘까지는 포함
          return maturityDate >= today;
        })
      : [];

    // 요약 합계 (정수 표기)
    const totalPrincipalNoDecimals = eligibleProductsForSummary.reduce((sum, product) => {
      const principal = parseFloat(String(product.principal || '0').replace(/[₩$,]/g, ''));
      return sum + (isNaN(principal) ? 0 : principal);
    }, 0);
    const totalCurrentBalanceNoDecimals = eligibleProductsForSummary.reduce((sum, product) => {
      const balance = parseFloat(String(product['Current Balance'] || '0').replace(/[₩$,]/g, ''));
      return sum + (isNaN(balance) ? 0 : balance);
    }, 0);
    const summaryCurrencySymbol =
      eligibleProductsForSummary
        .map(detectProductCurrencySymbol)
        .find(Boolean) ||
      products
        .map(detectProductCurrencySymbol)
        .find(Boolean) ||
      fallbackSummaryCurrency;

    return (
      <div className="investment-container">
        <div className="investment-summary">
          <div className="summary-card">
            <h3>Total Products</h3>
            <span className="summary-value">
              {products.filter(product => {
                const principal = parseFloat(String(product.principal || '0').replace(/[₩$,]/g, ''));
                if (isNaN(principal) || principal === 0) return false;
                
                // 만기일이 지난 상품은 제외
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const maturityRaw = product['maturity date'] || product['Maturity Date'] || product['maturity_date'];
                
                if (maturityRaw) {
                  const maturityDate = new Date(maturityRaw);
                  if (!isNaN(maturityDate)) {
                    maturityDate.setHours(0, 0, 0, 0);
                    if (maturityDate < today) return false; // 만료된 상품 제외
                  }
                }
                
                return true;
              }).length}
            </span>
          </div>
          <div className="summary-card">
            <h3>Total Principal</h3>
            <span className="summary-value">
              {summaryCurrencySymbol}
              {Number(totalPrincipalNoDecimals).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          </div>
          <div className="summary-card">
            <h3>Total Current Balance</h3>
            <span className="summary-value">
              {summaryCurrencySymbol}
              {Number(totalCurrentBalanceNoDecimals).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          </div>
        </div>

        <div className="products-grid">
          {products
            .filter(product => {
              // principal이 0이 아닌 상품만 표시
              const principal = parseFloat(String(product.principal || '0').replace(/[₩$,]/g, ''));
              if (isNaN(principal) || principal === 0) return false;
              
              // 만기일이 지난 상품은 완전히 숨김
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const maturityRaw = product['maturity date'] || product['Maturity Date'] || product['maturity_date'];
              
              if (maturityRaw) {
                const maturityDate = new Date(maturityRaw);
                if (!isNaN(maturityDate)) {
                  maturityDate.setHours(0, 0, 0, 0);
                  if (maturityDate < today) return false; // 만료된 상품 완전히 숨김
                }
              }
              
              return true;
            })
            .map((product, index) => (
            <div key={index} className="product-card">
              <div className="product-header">
                <div className="product-info">
                  <h3 className="product-name">{product.product}</h3>
                  <span className="product-type">{userData.type.replace('-', ' ').toUpperCase()}</span>
                </div>
              </div>
              
              <div className="product-details">
                <div className="detail-row">
                  <span className="detail-label">Principal:</span>
                  <span className="detail-value">{formatCurrency(product.principal)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Current Balance:</span>
                  <span className="detail-value">{formatCurrency(product['Current Balance'])}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Interest Amount:</span>
                  <span className="detail-value">{formatCurrency(product['Interest Amount'])}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Annual Rate:</span>
                  <span className="detail-value">{product['annual rate']}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Start Date:</span>
                  <span className="detail-value">{formatDate(product['start date'])}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Maturity Date:</span>
                  <span className="detail-value">{formatDate(product['maturity date'])}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Lockup Period:</span>
                  <span className="detail-value">{product['minimum lockup']} months</span>
                </div>
              </div>
            </div>
            ))
          }
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="dashboard-title-container">
            <h1 className="dashboard-title">{getTypeTitle(userData?.type || user?.type)}</h1>
            {/* 미리보기 모드 표시 (클릭 불가능) */}
            {user?.type === 'admin' && searchParams.get('hash_id') && (
              <div className="preview-mode-indicator">
                <span className="preview-icon">👁️</span>
                <span className="preview-text">
                  미리보기 모드 - 해시: {searchParams.get('hash_id').substring(0, 8)}...
                </span>
              </div>
            )}
          </div>
        </div>
        {/* Content based on type */}
        {userData?.type === 'legacy' ? renderLegacyDashboard() : renderInvestmentDashboard()}
      </div>
    </div>
  );
};

export default DashboardPage; 
